import { useState } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import ThemeSwitcher from './ThemeSwitcher'

const Layout = () => {
  const { user, logout } = useAuth()
  const { isNight } = useTheme()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Check if we're on the home page for transparent navbar
  const isHomePage = location.pathname === '/'

  const isActive = (path) => {
    if (location.pathname === path) {
      return isNight
        ? 'border-b-2 border-cyber-cyan text-cyber-cyan'
        : 'border-b-2 border-primary-600 text-primary-600'
    }
    return isNight ? 'text-gray-300 hover:text-cyber-cyan' : 'text-gray-700 hover:text-primary-600'
  }

  return (
    <div className={`min-h-screen flex flex-col ${isNight ? 'bg-void' : isHomePage ? 'bg-transparent' : 'bg-white'}`}>
      {/* Navigation */}
      <nav
        className={`transition-all duration-300 ${
          isNight
            ? 'glass-nav-night'
            : isHomePage
              ? 'glass-nav-transparent'
              : 'glass-nav'
        }`}
      >
        <div className="max-w-[90%] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            {/* Brand - VIGILANT EDGE text */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-2">
                <span className="text-xl sm:text-2xl font-bold tracking-wide font-orbitron">
                  <span className="text-cyber-cyan">VIGILANT</span>
                  <span className="ml-1 text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">EDGE</span>
                </span>
              </Link>
            </div>

            {/* Center Nav Links - Desktop */}
            <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
              <Link
                to="/"
                className={`inline-flex items-center px-2 py-1 text-base lg:text-lg font-medium transition-colors ${isActive('/')}`}
              >
                Home
              </Link>
              <Link
                to="/services"
                className={`inline-flex items-center px-2 py-1 text-base lg:text-lg font-medium transition-colors ${isActive('/services')}`}
              >
                Services
              </Link>
              <Link
                to="/contact"
                className={`inline-flex items-center px-2 py-1 text-base lg:text-lg font-medium transition-colors ${isActive('/contact')}`}
              >
                Contact
              </Link>
              {user && user.status === 'approved' && (
                <Link
                  to="/portal"
                  className={`inline-flex items-center px-2 py-1 text-base lg:text-lg font-medium transition-colors ${isActive('/portal')}`}
                >
                  Portal
                </Link>
              )}
              {user && user.role === 'admin' && (
                <Link
                  to="/admin"
                  className={`inline-flex items-center px-2 py-1 text-base lg:text-lg font-medium transition-colors ${isActive('/admin')}`}
                >
                  Admin
                </Link>
              )}
            </div>

            {/* Right side */}
            <div className="flex items-center space-x-4 lg:space-x-6">
              <ThemeSwitcher />
              {user ? (
                <>
                  <span className={`text-base lg:text-lg hidden lg:inline ${isNight ? 'text-gray-400' : 'text-gray-700'}`}>
                    Welcome, {user.firstName}
                  </span>
                  <button
                    onClick={logout}
                    className={`hidden sm:block text-base lg:text-lg font-medium transition-colors ${
                      isNight ? 'text-gray-300 hover:text-cyber-cyan' : 'text-gray-700 hover:text-primary-600'
                    }`}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className={`hidden sm:block text-base lg:text-lg font-medium transition-colors ${
                      isNight ? 'text-gray-300 hover:text-cyber-cyan' : 'text-gray-700 hover:text-primary-600'
                    }`}
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    className={`hidden sm:block ${
                      isNight
                        ? 'btn-neon text-sm lg:text-base'
                        : isHomePage
                          ? 'btn-glass text-sm lg:text-base'
                          : 'btn-primary text-sm lg:text-base'
                    }`}
                  >
                    Sign Up
                  </Link>
                </>
              )}

              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`md:hidden p-2 rounded-md ${
                  isNight ? 'text-gray-300 hover:text-cyber-cyan' : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className={`md:hidden pb-4 ${isNight ? 'border-t border-cyber-cyan/20' : 'border-t border-gray-200'}`}>
              <div className="flex flex-col space-y-2 pt-4">
                <Link
                  to="/"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2 text-base font-medium rounded-md ${isActive('/')}`}
                >
                  Home
                </Link>
                <Link
                  to="/services"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2 text-base font-medium rounded-md ${isActive('/services')}`}
                >
                  Services
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2 text-base font-medium rounded-md ${isActive('/contact')}`}
                >
                  Contact
                </Link>
                {user && user.status === 'approved' && (
                  <Link
                    to="/portal"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-2 text-base font-medium rounded-md ${isActive('/portal')}`}
                  >
                    Portal
                  </Link>
                )}
                {user && user.role === 'admin' && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`px-4 py-2 text-base font-medium rounded-md ${isActive('/admin')}`}
                  >
                    Admin
                  </Link>
                )}
                {!user && (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`px-4 py-2 text-base font-medium rounded-md ${
                        isNight ? 'text-gray-300' : 'text-gray-700'
                      }`}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setMobileMenuOpen(false)}
                      className={`px-4 py-2 text-base font-medium rounded-md ${
                        isNight ? 'text-cyber-cyan' : 'text-primary-600'
                      }`}
                    >
                      Sign Up
                    </Link>
                  </>
                )}
                {user && (
                  <button
                    onClick={() => {
                      logout()
                      setMobileMenuOpen(false)
                    }}
                    className={`px-4 py-2 text-base font-medium text-left rounded-md ${
                      isNight ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    Logout
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className={isNight ? 'glass-footer-night' : 'glass-footer'}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Brand */}
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isNight ? 'text-white font-orbitron' : 'text-white'}`}>
                {isNight ? 'VIGILANTEDGE' : 'VigilantEdge'}
              </h3>
              <p className={`text-sm ${isNight ? 'text-gray-400' : 'text-gray-400'}`}>
                {isNight
                  ? 'Next-generation security assessment. Breach testing. Threat analysis.'
                  : 'Professional security assessment services to protect your digital assets.'}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isNight ? 'text-white' : 'text-white'}`}>
                Quick Links
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/services" className={`text-sm hover:text-white ${isNight ? 'text-gray-400 hover:text-cyber-cyan' : 'text-gray-400'}`}>
                    Services
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className={`text-sm hover:text-white ${isNight ? 'text-gray-400 hover:text-cyber-cyan' : 'text-gray-400'}`}>
                    Contact
                  </Link>
                </li>
                <li>
                  <Link to="/portal" className={`text-sm hover:text-white ${isNight ? 'text-gray-400 hover:text-cyber-cyan' : 'text-gray-400'}`}>
                    Customer Portal
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isNight ? 'text-white' : 'text-white'}`}>
                Legal
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy-policy" className={`text-sm hover:text-white ${isNight ? 'text-gray-400 hover:text-cyber-cyan' : 'text-gray-400'}`}>
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className={`text-sm hover:text-white ${isNight ? 'text-gray-400 hover:text-cyber-cyan' : 'text-gray-400'}`}>
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className={`text-lg font-semibold mb-4 ${isNight ? 'text-white' : 'text-white'}`}>
                Contact
              </h3>
              <p className="text-sm text-gray-400">
                Email:{' '}
                <a
                  href="mailto:info@vigilantedge.tech"
                  className={`hover:text-white ${isNight ? 'text-cyber-cyan' : 'text-primary-400'}`}
                >
                  info@vigilantedge.tech
                </a>
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className={`border-t mt-8 pt-8 text-center ${isNight ? 'border-void-light' : 'border-gray-700'}`}>
            <p className={`text-xs ${isNight ? 'text-gray-500 tracking-wider' : 'text-gray-400'}`}>
              {isNight
                ? `© ${new Date().getFullYear()} VIGILANTEDGE // ALL RIGHTS RESERVED`
                : `© ${new Date().getFullYear()} VigilantEdge. All rights reserved.`}
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Layout
